import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, AlertCircle, Info, X } from 'lucide-react';
import { useToastStore, type Toast as ToastType } from './useToast';

const iconMap = {
    success: CheckCircle,
    error: AlertCircle,
    info: Info,
};

const colorMap = {
    success: 'bg-emerald-500/15 border-emerald-500/30 text-emerald-300',
    error: 'bg-red-500/15 border-red-500/30 text-red-300',
    info: 'bg-sky-500/15 border-sky-500/30 text-sky-300',
};

const iconColorMap = {
    success: 'text-emerald-400',
    error: 'text-red-400',
    info: 'text-sky-400',
};

const ToastItem = ({ toast }: { toast: ToastType }) => {
    const dismiss = useToastStore((s: { dismiss: (id: string) => void }) => s.dismiss);
    const Icon = iconMap[toast.type];

    useEffect(() => {
        const timer = setTimeout(() => dismiss(toast.id), toast.duration || 3500);
        return () => clearTimeout(timer);
    }, [toast.id, toast.duration, dismiss]);

    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] }}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl border backdrop-blur-xl shadow-lg ${colorMap[toast.type as keyof typeof colorMap]}`}
        >
            <Icon className={`w-5 h-5 flex-shrink-0 ${iconColorMap[toast.type as keyof typeof iconColorMap]}`} />
            <p className="text-sm font-medium flex-1">{toast.message}</p>
            <button
                onClick={() => dismiss(toast.id)}
                className="p-1 rounded-lg hover:bg-white/10 transition-colors flex-shrink-0"
                aria-label="Dismiss notification"
            >
                <X className="w-3.5 h-3.5 opacity-60" />
            </button>
        </motion.div>
    );
};

export const ToastContainer = () => {
    const toasts = useToastStore((s: { toasts: ToastType[] }) => s.toasts);

    return (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[100] w-full max-w-sm px-4 space-y-2 pointer-events-none">
            <AnimatePresence mode="popLayout">
                {toasts.map((toast) => (
                    <div key={toast.id} className="pointer-events-auto">
                        <ToastItem toast={toast} />
                    </div>
                ))}
            </AnimatePresence>
        </div>
    );
};
